import { useApolloClient } from "@apollo/client"
import {
  HomeResourceCopyConfigLinkConnectionsDocument,
  HomeResourceCopyConfigLinkConnectionsQueryHookResult,
  HomeResourceCopyConfigLinkConnectionsQueryResult,
  HomeResourceCopyConfigLinkConnectionsQueryVariables,
  HomeResourceCopyConfigLinkFragment,
  ResourceBackgroundType,
  ResourceCopyConfigLinkLocation,
  useExecuteDeferredResourceCopyConfigMutation,
  useHomeResourceCopyConfigLinkConnectionsQuery,
  useHomeResourceCopyConfigLinkRegistrationQuery,
  useShowHideResourceCopyConfigLinkMutation
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../../pathwright/PathwrightContext"
import { canAdministrate } from "../../../user/permissions"
import { useHomeRegistrationVars } from "../../registration/HomeRegistrationQueryVars"
import PathCard from "../path/RegistrationPathCard"
import AddPathPromptCard from "./AddPathPromptCard"
import useOnPathCopied from "./useOnPathCopied"

// Source of truth for query and cache reads (excluding user-specific positioningContext).
const resourceCopyConfigLinkConnectionsQueryVars = {
  first: 50,
  copiedTimeDateFilter: { eq: null },
  hideFromLocationsFilter: [
    { neq: ResourceCopyConfigLinkLocation.HomeScreen }
  ]
} satisfies Omit<
  HomeResourceCopyConfigLinkConnectionsQueryVariables,
  "positioningContext"
>

// Map the unused/active resource copy config links to prompts.
export function useResourceCopyConfigLinks(): [
  HomeResourceCopyConfigLinkFragment[],
  HomeResourceCopyConfigLinkConnectionsQueryHookResult
] {
  const pwContext = usePathwrightContext()
  const { positioningContext } = useHomeRegistrationVars()
  const query = useHomeResourceCopyConfigLinkConnectionsQuery({
    variables: {
      ...resourceCopyConfigLinkConnectionsQueryVars,
      positioningContext: positioningContext!
    },
    skip: !canAdministrate(pwContext) || !positioningContext
  })

  // Use for sorting uncopied links. Since we're sorting out copied links
  // at the query level, we're only expecting copied links to exist if added
  // directly to cache (which happens immediately after copying a link).
  const nowTime = new Date().getTime()

  // Map the copy links from edges.
  const copyLinks = (
    query.data?.resourceCopyConfigLinkConnections?.edges?.map(
      (e) => e?.node!
    ) || []
  )
    // Require a resource in order to copy (a copy config lacking a resource must be the parent copy config).
    // We could do this at the query level, but this is fine for this case.
    .filter((copyLink) => copyLink.resourceCopyConfig.resource)
    // Sort copied linkes to the back based on copied time, meaning the
    // more recently the link has been copied, the further down in the list it will appear
    // (making it closer to the actual registrations below).
    .sort((copyLink1, copyLink2) => {
      function getTime(copyLink: HomeResourceCopyConfigLinkFragment) {
        return copyLink.copiedTime
          ? new Date(copyLink.copiedTime).getTime()
          : nowTime
      }

      return getTime(copyLink2) - getTime(copyLink1)
    })

  return [copyLinks, query]
}

type CopyLinkAddPromptCardProps = {
  copyLink: HomeResourceCopyConfigLinkFragment
}

function CopyLinkAddPromptCard({ copyLink }: CopyLinkAddPromptCardProps) {
  const client = useApolloClient()
  const { positioningContext } = useHomeRegistrationVars()
  const [copy, copyState] = useExecuteDeferredResourceCopyConfigMutation()
  // User has initiated bg copy task when mutation has 'executed' keys.
  // There should only be 1 key (1 bg task).
  const executedBgTaskId = Object.values(
    copyState.data?.executeDeferredResourceCopyConfig?.execute || {}
  )[0]

  const [hideLinkFromHomeScreen] = useShowHideResourceCopyConfigLinkMutation({
    variables: {
      id: copyLink.id,
      hide: [ResourceCopyConfigLinkLocation.HomeScreen]
    }
  })

  // Once the link has been copied, a registratino *should* exist.
  // It may not exist if the copy config wasn't set to assign a role to the user.
  // In that case, the UX will need to change (currently user will just see
  // indefinite loading indication).
  const { copierRegistration } = copyLink

  // Submitting when either the copy mutation is loading or we are awaiting the success
  // of the bg copy task.
  const isSubmitting = copyState.loading || !!executedBgTaskId

  // Let's poll for if the link has been copied when a background task exists.
  const shouldPollForCopiedLink = !!executedBgTaskId && !copierRegistration

  useHomeResourceCopyConfigLinkRegistrationQuery({
    variables: {
      id: copyLink.id,
      positioningContext: positioningContext!
    },
    skip: !shouldPollForCopiedLink || !positioningContext,
    pollInterval: 1000,
    fetchPolicy: "cache-and-network"
  })

  // Remove copy link from copy links list.
  function rmCopyLinkFromCache() {
    const query = HomeResourceCopyConfigLinkConnectionsDocument
    const variables = {
      ...resourceCopyConfigLinkConnectionsQueryVars,
      positioningContext: positioningContext!
    }

    const data = client.readQuery({
      query,
      variables
    }) as HomeResourceCopyConfigLinkConnectionsQueryResult["data"]

    const nextData: HomeResourceCopyConfigLinkConnectionsQueryResult["data"] = {
      ...data,
      resourceCopyConfigLinkConnections: {
        ...data?.resourceCopyConfigLinkConnections,
        // Filtering out the copied copy link.
        edges:
          data?.resourceCopyConfigLinkConnections?.edges?.filter(
            (e) => e?.node?.id !== copyLink.id
          ) ?? []
      }
    }

    client.writeQuery({
      query,
      variables,
      data: nextData
    })
  }

  useOnPathCopied(copierRegistration, rmCopyLinkFromCache)

  return copierRegistration ? (
    // NOTE: copying collections isn't supported yet, and we're not taking that
    // into account here, just passing the single registration created from the link.
    <PathCard registration={copierRegistration} />
  ) : (
    <AddPathPromptCard
      item={{
        pathName: copyLink.resourceCopyConfig.resource?.meta.title!,
        // We don't have the exact resource theme data so we provide base on the node meta which
        // will be close, but not always accurate. We could try getting the actual resource data
        // but the GQL API is currently scoping the resource schema to the current school and this
        // is cross-school path copying. We'd have to remove that constraint and add the Resource
        // schema to the ResourceCopyConfigLink schema.
        pathBackground: {
          background_image:
            copyLink.resourceCopyConfig.resource?.meta?.image?.src || "",
          cover_image:
            copyLink.resourceCopyConfig.resource?.meta?.image?.src || "",
          background_type: ResourceBackgroundType.Blur,
          background_overlay: null
        }
      }}
      isSubmitting={isSubmitting}
      onSubmit={async () => {
        await copy({
          variables: {
            linkId: copyLink.id
          }
        })
      }}
      onCancel={() => {
        rmCopyLinkFromCache()
        hideLinkFromHomeScreen()
      }}
    />
  )
}

export default CopyLinkAddPromptCard
